/**
 * Typed FastAPI client for the AI astrologer chat (POST /ai/complete).
 *
 * Auth: session-gated. We exchange initData → session JWT (ensureSessionToken),
 * read the user's birth data (fetchMe) and forward it as `user_profile` so the
 * server-built astrologer persona can reference the user's chart. The server
 * owns the system prompt + every guardrail (injection scan, cost guard, output
 * scan); the client only sends user/assistant turns.
 *
 * Data-correctness: the reply is the live LLM text. MOCK_MODE returns a single
 * honest "demo mode" line — never fabricated astrology positions.
 */
import { z } from "zod";

import { API_BASE, ApiError, apiFetch, ensureSessionToken, fetchMe, MOCK_MODE } from "@/lib/api";

export type WireChatRole = "user" | "assistant";

export interface WireChatMessage {
  readonly role: WireChatRole;
  readonly content: string;
}

/** Mirrors FastAPI `AIChatCompleteResponse` (services/api/.../schemas/ai.py). */
const aiCompleteSchema = z.object({
  content: z.string(),
  model: z.string(),
  stop_reason: z.string(),
  input_tokens: z.number().int().nonnegative(),
  output_tokens: z.number().int().nonnegative(),
});
export type AiComplete = z.infer<typeof aiCompleteSchema>;

const MOCK_REPLY: AiComplete = {
  content: "Демо-режим (VITE_MOCK_API=1): реальные ответы астролога приходят на живом API.",
  model: "mock",
  stop_reason: "stop",
  input_tokens: 0,
  output_tokens: 0,
};

function delay<T>(value: T, ms = 200): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

/**
 * Send the conversation to the astrologer and return its reply.
 *
 * `intent: "general"` loads the full Hellenistic astrologer persona server-side;
 * the forwarded `user_profile` lets it reference the user's chart. Birth fields
 * are nullable — a not-yet-onboarded user still gets general answers. `history`
 * must end with the user's latest turn (the server rejects a trailing non-user
 * message).
 */
export async function sendAiMessage(
  initData: string | null,
  history: readonly WireChatMessage[],
): Promise<AiComplete> {
  if (MOCK_MODE) return delay(MOCK_REPLY);

  const token = await ensureSessionToken(initData);
  const me = await fetchMe(initData);
  const userProfile = {
    first_name: me.first_name ?? null,
    birth_date: me.birth_date ?? null,
    birth_time: me.birth_time ?? null,
    birth_lat: me.birth_lat ?? null,
    birth_lon: me.birth_lon ?? null,
    timezone: me.timezone ?? null,
    locale: "ru",
  };
  return apiFetch({
    path: "/ai/complete",
    method: "POST",
    token,
    body: {
      intent: "general",
      messages: history,
      user_profile: userProfile,
      max_tokens: 800,
    },
    schema: aiCompleteSchema,
  });
}

/* ----- Streaming (POST /ai/stream) ----- */

/**
 * Why the answer arrives in pieces.
 *
 * `done`    — the model finished normally.
 * `blocked` — the server's output guard caught a canary/secret leak mid-answer
 *             and cut the stream. The partial text is discarded, not shown.
 * `limit`   — the per-user token budget ran out mid-answer.
 */
export type AiStreamStop = "done" | "blocked" | "limit";

export interface AiStreamResult {
  readonly text: string;
  readonly stop: AiStreamStop;
}

/** Server sentinels — whole SSE events, never fragments of the answer. */
const SENTINELS: Readonly<Record<string, AiStreamStop>> = {
  "[DONE]": "done",
  "[BLOCKED]": "blocked",
  "[LIMIT_EXCEEDED]": "limit",
};

/**
 * Join one SSE event's `data:` fields into its text payload.
 *
 * The server frames every line of a chunk as its own `data:` field (HTML spec
 * §9.2.6), so the payload is the fields joined by "\n" — concatenating them
 * would silently eat the newlines in a multi-paragraph answer. Per spec a
 * single space after the colon is part of the framing, not the data.
 */
function eventPayload(rawEvent: string): string | null {
  const fields = rawEvent
    .split("\n")
    .filter((line) => line.startsWith("data:"))
    .map((line) => {
      const value = line.slice("data:".length);
      return value.startsWith(" ") ? value.slice(1) : value;
    });
  return fields.length > 0 ? fields.join("\n") : null;
}

/**
 * Stream the astrologer's answer, calling `onDelta` with each piece as it
 * lands, and resolving with the full text once the server closes the stream.
 *
 * Chunk boundaries fall mid-word, so pieces are concatenated with NO separator
 * between events (the newlines live inside an event, see `eventPayload`).
 *
 * `blocked` and `limit` resolve rather than throw: they are server decisions
 * about this answer, not transport failures, and the screen has specific copy
 * for each. Network and auth failures still throw.
 */
export async function streamAiMessage(
  initData: string | null,
  history: readonly WireChatMessage[],
  onDelta: (delta: string) => void,
  signal?: AbortSignal,
): Promise<AiStreamResult> {
  if (MOCK_MODE) {
    for (const piece of [
      "Демо-режим (VITE_MOCK_API=1): ",
      "реальные ответы астролога ",
      "приходят на живом API.",
    ]) {
      await delay(null, 120);
      onDelta(piece);
    }
    return { text: MOCK_REPLY.content, stop: "done" };
  }

  const token = await ensureSessionToken(initData);
  const me = await fetchMe(initData);

  const res = await fetch(`${API_BASE}/ai/stream`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      intent: "general",
      messages: history,
      user_profile: {
        first_name: me.first_name ?? null,
        birth_date: me.birth_date ?? null,
        birth_time: me.birth_time ?? null,
        birth_lat: me.birth_lat ?? null,
        birth_lon: me.birth_lon ?? null,
        timezone: me.timezone ?? null,
        locale: "ru",
      },
      max_tokens: 800,
    }),
    ...(signal ? { signal } : {}),
  });

  if (!res.ok) {
    // Guard rejections (422) and rate limits (429) arrive as JSON, not SSE.
    const body = await res.text();
    throw new ApiError(`AI stream failed (${res.status})`, res.status, body);
  }
  if (!res.body) {
    throw new ApiError("AI stream returned no body", res.status, null);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  // Held in an object so TypeScript does not narrow `stop` to its initialiser:
  // the only writes happen inside the `consume` closure below.
  const state: { text: string; stop: AiStreamStop } = { text: "", stop: "done" };

  const consume = (rawEvent: string): boolean => {
    const payload = eventPayload(rawEvent);
    if (payload === null) return false;
    const sentinel = SENTINELS[payload];
    if (sentinel !== undefined) {
      state.stop = sentinel;
      return true; // stop reading
    }
    state.text += payload;
    onDelta(payload);
    return false;
  };

  try {
    let finished = false;
    while (!finished) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // Events are separated by a blank line; the tail may be a partial event.
      let sep = buffer.indexOf("\n\n");
      while (sep !== -1) {
        const rawEvent = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        if (consume(rawEvent)) {
          finished = true;
          break;
        }
        sep = buffer.indexOf("\n\n");
      }
    }
    // A server that closed without a trailing blank line still owes us its
    // last event — don't drop the final sentence.
    if (!finished && buffer.trim().length > 0) consume(buffer);
  } finally {
    await reader.cancel().catch(() => undefined);
  }

  // A blocked answer must not be shown even partially — that is the whole
  // point of the server-side output guard.
  return { text: state.stop === "blocked" ? "" : state.text, stop: state.stop };
}
